import React, { useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { FilterMatchMode } from 'primereact/api';
import { dateOnlyBodyTemplate } from '../../../../utils/dateUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';

// hooks/useTrainingRequestData.js'teki gruplarla birebir aynı — bkz. oradaki not.
const EXACT_FILTER_FIELDS = ['vehicleModel', 'trainingType', 'location'];
const TEXT_FILTER_FIELDS = ['numberOfParticipants', 'trainingLanguage', 'status', 'createdName'];
const DATE_FILTER_FIELDS = ['preferTrainingDate', 'alternativeDate', 'createdDate'];

/**
 * Eğitim Talebi listesi — projedeki "statik kolon filtresi" deseni (`filterDisplay="row"`,
 * her kolon başlığının altında inline filtre) TÜM kolonlara uygulanıyor; bkz. warranty-claims
 * modülündeki `WarrantyClaimsTable.jsx` referans desen. `vehicleModelText`/`trainingTypeText`
 * gibi okunabilir alanlar backend'in `GetById` cevabında var (bkz. docs/lms-create-request.md);
 * `docs/lms-create-request-fe-notu.txt`'e göre GetList'in `items[]`'i artık GetById ile AYNI
 * nesneyi döndürüyor, o yüzden liste satırında da bu alanlar mevcut kabul ediliyor. (`status`
 * için ne bu dokümanda ne fe-notu'nda bir `statusText` alanı var — kolon ham kodu (0, 2, ...)
 * olduğu gibi gösteriyor, bkz. docs/LMS-Egitim-Talebi-BE-Istek-Notu.md §3.)
 */
const TrainingRequestTable = ({
    data,
    loading,
    onPage,
    lazyParams,
    totalCount,
    onEdit,
    activeFilters,
    onFiltersChange,
    vehicleModelOptions = [],
    trainingTypeOptions = [],
    trainingLocationOptions = [],
    sortField,
    sortOrder,
    onSortChange
}) => {
    const { th } = useLanguage();

    const normalizedData = useMemo(() => {
        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.items)) return data.items;
        return [];
    }, [data]);

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const tableFilters = useMemo(() => {
        const filters = {};
        EXACT_FILTER_FIELDS.forEach((field) => {
            filters[field] = { value: activeFilters?.[field] ?? null, matchMode: FilterMatchMode.EQUALS };
        });
        TEXT_FILTER_FIELDS.forEach((field) => {
            filters[field] = { value: activeFilters?.[field] ?? null, matchMode: activeFilters?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS };
        });
        DATE_FILTER_FIELDS.forEach((field) => {
            filters[field] = { value: activeFilters?.[field] ?? null, matchMode: FilterMatchMode.DATE_IS };
        });
        return filters;
    }, [activeFilters]);

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};
        const updated = { ...(activeFilters || {}) };

        [...EXACT_FILTER_FIELDS, ...DATE_FILTER_FIELDS].forEach((field) => {
            if (field in filters) updated[field] = filters[field]?.value ?? null;
        });
        TEXT_FILTER_FIELDS.forEach((field) => {
            if (field in filters) {
                updated[field] = filters[field]?.value ?? null;
                updated[`${field}MatchMode`] = filters[field]?.matchMode ?? FilterMatchMode.CONTAINS;
            }
        });

        onFiltersChange?.(updated);
    };

    const handleSort = (e) => {
        onSortChange?.(e.sortField, e.sortOrder);
    };

    const vehicleModelFilterTemplate = (options) => (
        <Dropdown value={options.value} options={vehicleModelOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear filter style={{ width: '100%', minWidth: '10rem' }} />
    );

    const trainingTypeFilterTemplate = (options) => (
        <Dropdown value={options.value} options={trainingTypeOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear filter style={{ width: '100%', minWidth: '10rem' }} />
    );

    const locationFilterTemplate = (options) => (
        <Dropdown value={options.value} options={trainingLocationOptions} optionLabel="label" optionValue="value" onChange={(e) => options.filterApplyCallback(e.value)} placeholder={th('common.search')} className="p-column-filter w-full" showClear filter style={{ width: '100%', minWidth: '10rem' }} />
    );

    // Tarih filtresi: warranty-claims'teki dateFilterTemplate ile aynı desen — seçilen günü
    // yerel saat diliminde 00:00:00'a normalize eder (UTC kaymasını önlemek için).
    const dateFilterTemplate = (options) => {
        const handleDateChange = (e) => {
            let normalizedDate = e.value;
            if (normalizedDate instanceof Date) {
                normalizedDate = new Date(normalizedDate.getFullYear(), normalizedDate.getMonth(), normalizedDate.getDate());
            }
            options.filterApplyCallback(normalizedDate);
        };
        return <Calendar value={options.value} onChange={handleDateChange} dateFormat="dd.mm.yy" placeholder={th('common.search')} showIcon className="p-column-filter w-full" style={{ width: '100%', minWidth: '11rem' }} inputStyle={{ width: '100%' }} />;
    };

    // GetList/GetById'daki locations[] artık { locationCode, locationText } şeklinde
    // (docs/lms-create-request-fe-notu.txt kırıcı değişikliği — eskiden { code, text }).
    const locationsBodyTemplate = (rowData) => {
        if (!Array.isArray(rowData.locations) || rowData.locations.length === 0) return '';
        return rowData.locations
            .map((loc) => loc?.locationText || loc?.locationCode)
            .filter(Boolean)
            .join(', ');
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center">
            <i className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')} onClick={() => onEdit(rowData)} />
        </div>
    );

    return (
        <DataTable
            className="p-datatable-gridlines"
            value={normalizedData}
            paginator
            rows={lazyParams.rows || 10}
            loading={loading}
            totalRecords={totalCount}
            showGridlines
            lazy
            scrollable
            scrollDirection="horizontal"
            first={lazyParams.first}
            onPage={onPage}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            dataKey="requestId"
            emptyMessage={th('trainingRequest.emptyMessage')}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            tableStyle={{ minWidth: '130rem' }}
            sortMode="single"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        >
            <Column body={actionBodyTemplate} style={{ width: '4rem' }} align="center" frozen />
            <Column
                field="vehicleModel"
                header={th('trainingRequest.columns.vehicleModel')}
                style={{ width: '11rem' }}
                body={(row) => row.vehicleModelText || row.vehicleModel}
                frozen
                filter
                filterField="vehicleModel"
                filterElement={vehicleModelFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="trainingType"
                header={th('trainingRequest.columns.trainingType')}
                style={{ width: '11rem' }}
                body={(row) => row.trainingTypeText || row.trainingType}
                filter
                filterField="trainingType"
                filterElement={trainingTypeFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="numberOfParticipants"
                header={th('trainingRequest.columns.participants')}
                style={{ width: '12rem' }}
                filter
                filterField="numberOfParticipants"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
            <Column
                field="preferTrainingDate"
                header={th('trainingRequest.columns.preferDate')}
                style={{ width: '11rem' }}
                body={(row) => dateOnlyBodyTemplate(row, 'preferTrainingDate')}
                sortable
                filter
                filterField="preferTrainingDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="alternativeDate"
                header={th('trainingRequest.columns.alternativeDate')}
                style={{ width: '11rem' }}
                body={(row) => dateOnlyBodyTemplate(row, 'alternativeDate')}
                filter
                filterField="alternativeDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                header={th('trainingRequest.columns.location')}
                style={{ width: '11rem' }}
                body={locationsBodyTemplate}
                filter
                filterField="location"
                filterElement={locationFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="trainingLanguage"
                header={th('trainingRequest.columns.language')}
                style={{ width: '12rem' }}
                filter
                filterField="trainingLanguage"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
            <Column
                field="status"
                header={th('trainingRequest.columns.status')}
                style={{ width: '11rem' }}
                filter
                filterField="status"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
            <Column
                field="createdDate"
                header={th('trainingRequest.columns.createdDate')}
                style={{ width: '11rem' }}
                body={(row) => dateOnlyBodyTemplate(row, 'createdDate')}
                sortable
                filter
                filterField="createdDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
            />
            <Column
                field="createdName"
                header={th('trainingRequest.columns.createdName')}
                style={{ width: '12rem' }}
                filter
                filterField="createdName"
                filterPlaceholder={th('common.search')}
                showFilterMenu
                showClearButton={false}
                filterMatchModeOptions={textMatchModeOptions}
            />
        </DataTable>
    );
};

export default TrainingRequestTable;
